import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { globalSearch, type SearchHit } from "@/lib/api";
import { isConfigured } from "@/lib/supabase";
import { cx } from "@/components/ui/primitives";

const KIND_LABEL: Record<SearchHit["kind"], string> = {
  team: "Franchise", player: "Joueur", staff: "Staff", season: "Saison", game: "Match",
};

function hrefFor(h: SearchHit): string {
  switch (h.kind) {
    case "team": return `/equipes/${h.slug}`;
    case "player": return `/joueurs/${h.id}`;
    case "staff": return `/staff/${h.slug}`;
    case "season": return `/saisons/${h.id}`;
    case "game": return `/matchs/${h.id}`;
  }
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function SearchTrigger({ onOpen }: { onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex h-10 w-full items-center gap-2 rounded-lg border border-line-strong bg-surface-2 px-3 text-left text-sm text-faint hover:border-faint md:w-80"
      aria-label="Rechercher (raccourci Ctrl+K)"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" strokeLinecap="round" /></svg>
      <span className="flex-1 truncate">Franchise, joueur, saison, « LAL BOS »…</span>
      <kbd className="hidden rounded border border-line-strong px-1.5 font-mono text-[0.65rem] text-muted sm:inline">Ctrl K</kbd>
    </button>
  );
}

export function GlobalSearch({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const navigate = useNavigate();
  const listId = useId();
  const term = useDebounced(q.trim(), 180);

  const { data, isFetching, isError } = useQuery({
    queryKey: ["search", term],
    queryFn: () => globalSearch(term),
    enabled: isConfigured && open && term.length >= 2,
    staleTime: 60_000,
  });
  const hits = useMemo(() => data ?? [], [data]);

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (open && !d.open) {
      d.showModal();
      setTimeout(() => inputRef.current?.focus(), 0);
    } else if (!open && d.open) d.close();
  }, [open]);

  useEffect(() => setActive(0), [term]);

  const go = (h: SearchHit) => {
    onClose();
    setQ("");
    navigate(hrefFor(h));
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, hits.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    if (e.key === "Enter" && hits[active]) { e.preventDefault(); go(hits[active]); }
  };

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      onClick={(e) => { if (e.target === dialogRef.current) onClose(); }}
      aria-label="Recherche globale"
      className="m-0 mx-auto mt-[8vh] w-[min(680px,calc(100vw-24px))] max-w-none rounded-2xl border border-line-strong bg-surface p-0 text-ink shadow-2xl backdrop:bg-black/60 backdrop:backdrop-blur-sm"
    >
      <div className="flex items-center gap-3 border-b border-line px-4">
        <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden fill="none" stroke="currentColor" strokeWidth="2" className="text-muted"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" strokeLinecap="round" /></svg>
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-expanded={hits.length > 0}
          aria-controls={listId}
          aria-activedescendant={hits[active] ? `${listId}-${active}` : undefined}
          aria-autocomplete="list"
          placeholder="Rechercher une franchise, un joueur, une saison, un entraîneur, un match…"
          className="h-14 flex-1 bg-transparent text-[1.05rem] outline-none placeholder:text-faint"
        />
        <button type="button" onClick={onClose} className="rounded-md border border-line-strong px-2 py-0.5 font-mono text-[0.65rem] text-muted">Échap</button>
      </div>
      <div className="max-h-[60vh] overflow-y-auto p-2">
        {!isConfigured && <p className="p-4 text-sm text-warn">La recherche nécessite une base de données connectée.</p>}
        {isConfigured && term.length < 2 && (
          <div className="p-4 text-sm text-muted">
            <p className="mb-2">Exemples :</p>
            <ul className="flex flex-wrap gap-2">
              {["Celtics", "SuperSonics", "2015-16", "LAL BOS", "Jokic"].map((ex) => (
                <li key={ex}><button type="button" onClick={() => setQ(ex)} className="rounded-full border border-line-strong px-3 py-1 hover:border-accent hover:text-accent-text">{ex}</button></li>
              ))}
            </ul>
          </div>
        )}
        {isConfigured && term.length >= 2 && !isFetching && hits.length === 0 && !isError && (
          <p className="p-4 text-sm text-muted">Aucun résultat pour « {term} » dans les données synchronisées.</p>
        )}
        {isError && <p className="p-4 text-sm text-neg">La recherche a échoué.</p>}
        <ul id={listId} role="listbox" aria-label="Résultats">
          {hits.map((h, i) => (
            <li
              key={`${h.kind}-${h.id}-${h.label}`}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              onMouseEnter={() => setActive(i)}
              onClick={() => go(h)}
              className={cx("flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5", i === active ? "bg-surface-3" : "")}
            >
              <span className="w-20 shrink-0 font-mono text-[0.62rem] uppercase tracking-wider text-accent-text">{KIND_LABEL[h.kind]}</span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-semibold">{h.label}</span>
                <span className="block truncate text-xs text-muted">{h.sublabel}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </dialog>
  );
}
